// Eskom PARCC — drive a polled AI review run from the CES screen.
//
// The server does one batch per request and never works between them, so the
// client is the loop. That is deliberate: Cloud Run throttles CPU after a
// response and the next call may land on a different instance, so a detached
// server-side loop would be neither reliable nor observable.
//
// THREE THINGS HERE ARE ABOUT MONEY, and they are the reason this is a hook
// rather than a `useEffect` in the panel:
//
//   1. A run survives a remount. `/start` and every `/step` are billed; leaving
//      the CES screen and coming back must resume or rehydrate, never restart.
//   2. A lost response is not a failed run. The ~60s Hosting ceiling becomes
//      rare, not impossible, and the failure it produces is "the batch completed
//      and was billed, but the reply never arrived". Re-reading the run and
//      checking whether the cursor advanced is what turns that into a non-event.
//   3. Cancelling stops the NEXT batch. The one in flight is already paid for,
//      so the control says so rather than implying an instant stop.
import { useCallback, useEffect, useRef, useState } from 'react';
import { adminApi, type AiRun, type AiProposal, type AiReviewResult } from '../../lib/adminApi';

/**
 * Present a durable, quote-free result as if it were a finished run, so the panel
 * renders it through the same 'done' path a live run uses. It carries no live
 * runId to drive and cannot be stepped — it is a record, not a run.
 */
function resultToRun(result: AiReviewResult): AiRun {
  const proposals: AiProposal[] = [
    ...(JSON.parse(result.proposalsJson || '[]') as AiProposal[]),
    ...((result.holisticJson ? JSON.parse(result.holisticJson) : []) as AiProposal[]),
  ];
  // Recover the coverage accounting the durable result now persists, so a
  // rehydrated/reused review shows the SAME "N discarded / K not assessed /
  // reviewer-only" picture a live run does — the gap that made the 13 Aug run's
  // blanks indistinguishable from failures. Absent on pre-accounting results.
  let rejected: AiRun['rejected'] = [];
  let reviewerOnly: AiRun['reviewerOnly'] = [];
  try {
    if (result.accountingJson) {
      const acc = JSON.parse(result.accountingJson) as {
        rejected?: AiRun['rejected'];
        reviewerOnly?: AiRun['reviewerOnly'];
      };
      rejected = acc.rejected ?? [];
      reviewerOnly = acc.reviewerOnly ?? [];
    }
  } catch {
    // A malformed blob is a display degradation, not a failure — show the
    // proposals we have rather than throwing away the whole rehydration.
  }
  return {
    schemaVersion: 1,
    runId: result.runId,
    referenceNo: result.referenceNo,
    status: 'done',
    cancelRequested: false,
    cursor: 0,
    totalBatches: 0,
    batchSize: 0,
    createdAt: result.createdAt,
    updatedAt: result.finishedAt ?? result.createdAt,
    finishedAt: result.finishedAt,
    elapsedMs: 0,
    steps: [],
    proposals,
    rejected,
    reviewerOnly,
    flaggedDocuments: [],
    refused: false,
    batchesRun: result.model
      ? [{ index: 0, model: result.model, provider: null, ms: 0, outcome: 'ok' }]
      : [],
  };
}

/** Where a run id is remembered across a remount.
 *
 *  sessionStorage, NOT localStorage: a run belongs to this tab and this sitting,
 *  and localStorage is where `uiPrefs` lives — which aiMode.ts explicitly treats
 *  as user-writable and untrusted. A run id confers nothing (the server
 *  re-derives every authorisation), but it should still not outlive the session
 *  that paid for it. */
const runKey = (referenceNo: string) => `parcc.aiRun.${referenceNo}`;

export type AiRunState =
  | { kind: 'idle' }
  | { kind: 'starting' }
  | { kind: 'running'; run: AiRun }
  /** A run the reviewer left to finish server-side. The client POLLS to show
   *  progress if they stay, but never drives a batch — the scheduler sweep does
   *  the spending, and completion emails the owner. */
  | { kind: 'background'; run: AiRun }
  /** Another tab holds the lease, or a step is being retried. */
  | { kind: 'stalled'; run: AiRun; message: string }
  /** `source` distinguishes a run just completed here ('live') from a durable
   *  result shown without spending: reused because the pack was unchanged
   *  ('reused'), or rehydrated from an earlier sitting on mount ('rehydrated'). */
  | { kind: 'done'; run: AiRun; source?: 'live' | 'reused' | 'rehydrated' }
  /** Note the run is RETAINED — a nine-batch run that fails on batch seven still
   *  holds six batches of paid-for, valid proposals and a trace worth reading. */
  | { kind: 'error'; message: string; run: AiRun | null };

export interface AiReviewRun {
  state: AiRunState;
  start: (
    criteria: { seq: number; label: string }[],
    opts?: { background?: boolean; force?: boolean },
  ) => Promise<void>;
  cancel: () => Promise<void>;
  reset: () => void;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function useAiReviewRun(referenceNo: string, enabled = true): AiReviewRun {
  const [state, setState] = useState<AiRunState>({ kind: 'idle' });
  // A ref, not state: the loop must see an abort immediately, and it must not
  // re-render on every tick.
  const abortRef = useRef<AbortController | null>(null);
  const runIdRef = useRef<string | null>(null);

  const remember = useCallback(
    (runId: string | null) => {
      runIdRef.current = runId;
      try {
        if (runId) sessionStorage.setItem(runKey(referenceNo), runId);
        else sessionStorage.removeItem(runKey(referenceNo));
      } catch {
        // Private mode / storage disabled. The run still works; it just will not
        // survive a reload, which is a degradation rather than a failure.
      }
    },
    [referenceNo],
  );

  /** Drive batches until the run ends. Each call IS a batch, so there is no
   *  interval to tune and never a wasted request. */
  const drive = useCallback(
    async (runId: string, signal: AbortSignal) => {
      let retries = 0;
      let retryables = 0;
      for (;;) {
        if (signal.aborted) return;
        let run: AiRun;
        try {
          run = await adminApi.aiReviewStep({ referenceNo, runId });
          retries = 0;
        } catch (err) {
          if (signal.aborted) return;
          const message = err instanceof Error ? err.message : 'That step failed.';

          // A 403 mid-run is authoritative: the grant was revoked, the kill
          // switch was thrown, or the transaction was reallocated. Stop, and say
          // exactly what the server said.
          if (/\(403\)/.test(message)) {
            setState((s) => ({ kind: 'error', message, run: 'run' in s ? s.run : null }));
            return;
          }

          // THE IMPORTANT ONE. A 504 means the Hosting rewrite gave up — but the
          // batch may well have completed and been billed server-side. Re-read
          // the run: if the cursor moved, only the response was lost, and
          // failing here would discard work already paid for.
          try {
            const fresh = await adminApi.aiReviewRun(referenceNo, runId);
            if (fresh.status !== 'running') {
              setState({ kind: 'done', run: fresh });
              remember(null);
              return;
            }
            if (fresh.busy) {
              // Someone else is spending. Poll cheaply until the lease clears
              // rather than racing them for the same batch.
              setState({ kind: 'stalled', run: fresh, message: 'Another session is running this review.' });
              await sleep(3000);
              continue;
            }
            setState({ kind: 'running', run: fresh });
            // The batch COMPLETED and was billed; only the reply was lost. That
            // is not a failed attempt, so it must not consume the retry budget
            // or serve a backoff — carry straight on to the next batch.
            retries = 0;
            continue;
          } catch {
            /* the read failed too — fall through to the retry budget */
          }

          retries += 1;
          if (retries > 3) {
            setState((s) => ({ kind: 'error', message, run: 'run' in s ? s.run : null }));
            return;
          }
          // A step that died before its compare-and-swap left the cursor where
          // it was, so retrying the same batch is safe.
          await sleep([2000, 6000, 15000][retries - 1] ?? 15000);
          continue;
        }

        // Re-check after the await: a step takes tens of seconds, and the
        // reviewer may have navigated away during it. Publishing state from an
        // unmounted panel is harmless; issuing the NEXT paid batch is not.
        if (signal.aborted) return;
        if (run.busy) {
          setState({ kind: 'stalled', run, message: 'Another session is running this review.' });
          await sleep(3000);
          continue;
        }
        if (run.retryable) {
          // A batch that did no work (budget abort, or the model call threw). The
          // cursor did not advance, so retrying the SAME batch is safe — but this
          // is a SUCCESSFUL response, so it never touched the catch-block retry
          // budget. Without a cap and a backoff, a batch that always runs long
          // loops forever, burning a paid model call each time.
          retryables += 1;
          if (retryables > 4) {
            setState((s) => ({
              kind: 'error',
              message: 'A step repeatedly ran out of time. Try again later or reduce the criteria.',
              run: 'run' in s ? s.run : run,
            }));
            return;
          }
          setState({ kind: 'stalled', run, message: 'That step ran out of time — retrying.' });
          await sleep([3000, 8000, 15000, 30000][retryables - 1] ?? 30000);
          continue;
        }
        retryables = 0;
        if (run.status !== 'running') {
          setState({ kind: 'done', run });
          remember(null);
          return;
        }
        setState({ kind: 'running', run });
        // Yield so React paints the trace between batches.
        await sleep(250);
      }
    },
    [referenceNo, remember],
  );

  /** Watch a background run WITHOUT driving it. GET /run is free, so this only
   *  reflects the scheduler sweep's progress — it never calls /step, so it never
   *  spends. If the reviewer leaves, the sweep finishes the run and emails them. */
  const poll = useCallback(
    async (runId: string, signal: AbortSignal) => {
      for (;;) {
        if (signal.aborted) return;
        let run: AiRun;
        try {
          run = await adminApi.aiReviewRun(referenceNo, runId);
        } catch {
          // Transient read failure — retry rather than error a run the server is
          // still finishing in the background.
          await sleep(12000);
          continue;
        }
        if (signal.aborted) return;
        if (run.status !== 'running') {
          setState({ kind: 'done', run, source: 'live' });
          remember(null);
          return;
        }
        setState({ kind: 'background', run });
        await sleep(10000);
      }
    },
    [referenceNo, remember],
  );

  const start = useCallback(
    async (
      criteria: { seq: number; label: string }[],
      opts?: { background?: boolean; force?: boolean },
    ) => {
      const background = opts?.background === true;
      const force = opts?.force === true;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setState({ kind: 'starting' });
      try {
        const res = await adminApi.aiReviewStart({ referenceNo, criteria, background, force });
        // REUSE. An identical completed review already exists (unchanged pack) and
        // force was not set: nothing was spent and there is no live run to drive —
        // show the durable result and let the reviewer re-run if they want to.
        if ('reused' in res) {
          remember(null);
          setState({ kind: 'done', run: resultToRun(res.result), source: 'reused' });
          return;
        }
        const run = res;
        remember(run.runId);
        if (background || run.background) {
          // The client never drives a background run — the sweep does. Poll to
          // show progress if the reviewer stays; if they leave, the email finds
          // them.
          setState({ kind: 'background', run });
          await poll(run.runId, controller.signal);
        } else {
          setState({ kind: 'running', run });
          await drive(run.runId, controller.signal);
        }
      } catch (err) {
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : 'The review could not be started.',
          run: null,
        });
      }
    },
    [referenceNo, drive, poll, remember],
  );

  const cancel = useCallback(async () => {
    const runId = runIdRef.current;
    if (!runId) return;
    try {
      // Aborting the fetch does NOT stop the batch in flight — it is already
      // running and already billed. The server flag is what stops the next one.
      const run = await adminApi.aiReviewCancel({ referenceNo, runId });
      setState({ kind: 'running', run });
    } catch {
      /* the loop will surface any real failure */
    }
  }, [referenceNo]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    remember(null);
    setState({ kind: 'idle' });
  }, [remember]);

  // On mount, restore whatever the reviewer should see WITHOUT spending. In order:
  // adopt a run this tab already paid for (drive it, or poll if it is a background
  // run); else rehydrate the last COMPLETED review from the durable result — the
  // "come back on any device and see it" path, which also stops a reviewer whose
  // pointer is gone from starting (and re-billing) a second identical run.
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    abortRef.current = controller;
    const teardown = () => {
      cancelled = true;
      // ⚠️ ABORT THE CURRENT CONTROLLER, not this effect's own. `start()` replaces
      // `abortRef.current` with a controller this closure never saw, so aborting
      // `controller` here would leave a button-started run driving after unmount —
      // every subsequent batch billed for a screen nobody is looking at.
      abortRef.current?.abort();
      controller.abort();
    };
    if (!enabled) return teardown;

    let stored: string | null = null;
    try {
      stored = sessionStorage.getItem(runKey(referenceNo));
    } catch {
      stored = null;
    }

    const rehydrateLatest = async () => {
      try {
        const { result } = await adminApi.aiReviewLatest(referenceNo);
        if (cancelled || !result) return;
        setState({ kind: 'done', run: resultToRun(result), source: 'rehydrated' });
      } catch {
        /* no durable result yet, or the read failed — stay idle */
      }
    };

    void (async () => {
      if (stored) {
        const runId = stored;
        runIdRef.current = runId;
        try {
          const run = await adminApi.aiReviewRun(referenceNo, runId);
          if (cancelled) return;
          if (run.status === 'running') {
            if (run.background) {
              setState({ kind: 'background', run });
              await poll(runId, controller.signal);
            } else {
              setState({ kind: 'running', run });
              await drive(runId, controller.signal);
            }
          } else {
            setState({ kind: 'done', run, source: 'live' });
            remember(null);
          }
        } catch {
          // The stored run expired (7-day lifecycle) or is unreadable — drop the
          // pointer and fall back to the durable last result, so the reviewer
          // still sees their review rather than a blank panel.
          if (cancelled) return;
          remember(null);
          await rehydrateLatest();
        }
        return;
      }
      await rehydrateLatest();
    })();

    return teardown;
  }, [referenceNo, drive, poll, remember, enabled]);

  return { state, start, cancel, reset };
}
